import en from './messages/en.js';
import editorEn from './messages/editor.en.js';
import projectEn from './messages/project.en.js';

// A part of the app with many strings keeps them in its own file, under its own key.
// English is always here (every other language falls back to it); the others load
// when a page needs them (loadLocale). All four were in every page's first download,
// ~290 kB, where one is read (SEO_AUDIT.md, round 2).
export const MESSAGES = {
  en: { ...en, editor: editorEn, project: projectEn },
};

const LOADERS = {
  es: () => Promise.all([import('./messages/es.js'), import('./messages/editor.es.js'), import('./messages/project.es.js')]),
  pt: () => Promise.all([import('./messages/pt.js'), import('./messages/editor.pt.js'), import('./messages/project.pt.js')]),
  ar: () => Promise.all([import('./messages/ar.js'), import('./messages/editor.ar.js'), import('./messages/project.ar.js')]),
  fr: () => Promise.all([import('./messages/fr.js'), import('./messages/editor.fr.js'), import('./messages/project.fr.js')]),
  de: () => Promise.all([import('./messages/de.js'), import('./messages/editor.de.js'), import('./messages/project.de.js')]),
};
const loading = {};

/** Whether a locale's strings are here to be read. */
export const isLocaleLoaded = (locale) => Boolean(MESSAGES[locale]);

/** Fetch a locale's strings once; resolves at once for English or one already here. */
export function loadLocale(locale) {
  if (MESSAGES[locale] || !LOADERS[locale]) return Promise.resolve();
  loading[locale] ||= LOADERS[locale]().then(([main, editor, project]) => {
    MESSAGES[locale] = { ...main.default, editor: editor.default, project: project.default };
  }).catch((error) => {
    delete loading[locale]; // a dropped connection can be tried again
    throw error;
  });
  return loading[locale];
}

function lookup(messages, key) {
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), messages);
}

/** "{name}" placeholders filled from `vars`; unknown ones are left as written. */
export function format(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name) => (vars[name] ?? whole));
}

/**
 * t(key, vars) for one locale. A key the locale lacks falls back to English, so
 * a missing translation shows English rather than a key; a key nobody has shows
 * the key, which a test catches. Non-string values (lists, objects) come back
 * as they are, for copy that is structured.
 */
export function makeT(locale) {
  const messages = MESSAGES[locale] || MESSAGES.en;
  return (key, vars) => {
    let value = lookup(messages, key);
    if (value === undefined) value = lookup(MESSAGES.en, key);
    if (value === undefined) return key;
    return typeof value === 'string' ? format(value, vars) : value;
  };
}
